import { useEffect, useState } from 'react';

/**
 * Minimal pub/sub toast system for transactional feedback.
 *
 * Callers can publish from anywhere without threading context through the
 * review and admin component trees. Mount one <Toaster /> in each page shell.
 */

export type ToastKind = 'success' | 'error';

interface ToastMessage {
  id: number;
  kind: ToastKind;
  text: string;
}

type Listener = (toasts: ToastMessage[]) => void;
type Schedule = (callback: () => void, delay: number) => ReturnType<typeof globalThis.setTimeout>;

export function createToastStore(
  schedule: Schedule = (callback, delay) => globalThis.setTimeout(callback, delay),
) {
  let toasts: ToastMessage[] = [];
  let nextId = 1;
  const listeners = new Set<Listener>();

  function publish() {
    for (const listener of listeners) listener(toasts);
  }

  function dismiss(id: number) {
    toasts = toasts.filter((toast) => toast.id !== id);
    publish();
  }

  function show(kind: ToastKind, text: string) {
    const id = nextId++;
    toasts = [...toasts, { id, kind, text }];
    publish();
    schedule(() => dismiss(id), kind === 'error' ? 7_000 : 4_000);
  }

  function subscribe(listener: Listener) {
    listeners.add(listener);
    listener(toasts);
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    dismiss,
    getSnapshot: () => toasts,
    show,
    subscribe,
  };
}

const toastStore = createToastStore();

export function showToast(kind: ToastKind, text: string) {
  toastStore.show(kind, text);
}

export default function Toaster() {
  const [items, setItems] = useState(toastStore.getSnapshot);

  useEffect(() => toastStore.subscribe(setItems), []);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-5 z-[60] flex flex-col items-center gap-2 px-4"
    >
      {items.map((toast) => (
        <button
          key={toast.id}
          type="button"
          className={`rise-subtle pointer-events-auto flex w-full max-w-md items-center gap-2.5 rounded-input border bg-card px-4 py-2.5 text-left text-[13px] leading-snug shadow-card sm:w-auto ${
            toast.kind === 'success'
              ? 'border-accent text-fg'
              : 'border-line text-muted'
          }`}
          data-print-hide
          onClick={() => toastStore.dismiss(toast.id)}
          title="Dismiss"
        >
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              toast.kind === 'success' ? 'bg-accent' : 'bg-faint'
            }`}
            aria-hidden="true"
          />
          <span>{toast.text}</span>
        </button>
      ))}
    </div>
  );
}
